import { environmentVars } from 'constants/services';
import { History } from 'history';
import qs from 'qs';
import {
  areCredentialsValid,
  convertTokensToCredentials,
  getClaims,
} from '../credentials';
import { decodeState, encodeState, generateState } from '../state';
import { generatePkceChallenge } from '../pkce-challenge';

export class BbIdPKCEAuthService
  implements Bluebeam.Services.IAuthRefreshableService
{
  private _credentials: Bluebeam.Auth.Models.Credentials | undefined =
    undefined;

  constructor(
    private readonly _history: History,
    private readonly _deviceStorageService: Bluebeam.Services.IDeviceStorageService
  ) {}

  private _exchangeCodeForTokens = async (
    authCode: string
  ): Promise<Bluebeam.Auth.API.Tokens> => {
    const sanitizedAuthCode =
      typeof authCode === 'string' ? authCode.trim() : '';

    if (!sanitizedAuthCode) {
      throw new Error('The auth code is invalid');
    }

    const tokenEndpoint = `${environmentVars.BB_OPENID_ENDPOINT}/connect/token`;

    const codeVerifier = await this._deviceStorageService.getItem<string>(
      'bbid-pkce-code-verifier'
    );

    if (!codeVerifier) {
      throw new Error('Could not find code verifier in device storage.');
    }

    const formBody = new URLSearchParams();

    formBody.append('client_id', environmentVars.BB_OPENID_CLIENT_ID);
    formBody.append('client_secret', environmentVars.BB_OPENID_CLIENT_SECRET);
    formBody.append('code_verifier', codeVerifier);
    formBody.append('code', authCode);
    formBody.append('grant_type', 'authorization_code');
    formBody.append('redirect_uri', `${window.location.origin}/auth`);
    formBody.append('scope', 'openid offline_access profile email');

    const tokensResponse: Bluebeam.Auth.API.Tokens = await fetch(
      tokenEndpoint,
      {
        body: formBody.toString(),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        method: 'POST',
      }
    ).then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      return response.json();
    });

    const sanitizedAccessToken =
      typeof tokensResponse.access_token === 'string'
        ? tokensResponse.access_token.trim()
        : '';
    const sanitizedIdToken =
      typeof tokensResponse.id_token === 'string'
        ? tokensResponse.id_token.trim()
        : '';
    const sanitizedRefreshToken =
      typeof tokensResponse.refresh_token === 'string'
        ? tokensResponse.refresh_token.trim()
        : '';

    if (!sanitizedAccessToken) {
      throw new Error('The access token is invalid');
    }

    if (!sanitizedIdToken) {
      throw new Error('The id token is invalid');
    }

    if (!sanitizedRefreshToken) {
      throw new Error('The refresh token is invalid');
    }

    return {
      access_token: sanitizedAccessToken,
      id_token: sanitizedIdToken,
      refresh_token: sanitizedRefreshToken,
    };
  };

  getCredentials: Bluebeam.Services.IAuthRefreshableService['getCredentials'] =
    () => {
      if (!this._credentials) {
        throw new Error('The user is currently logged out.');
      }

      return this._credentials;
    };

  getSecondsUntilAccessTokenExpiration: Bluebeam.Services.IAuthRefreshableService['getSecondsUntilAccessTokenExpiration'] =
    () => {
      if (!this._credentials) {
        throw new Error('The user is currently logged out.');
      }

      const claims = getClaims(this._credentials.accessToken);

      if (!claims.exp || typeof claims.exp !== 'number') {
        throw new Error("The access token doesn't have an expiration time");
      }

      return claims.exp - Math.floor(Date.now() / 1000);
    };

  isLoggedIn: Bluebeam.Services.IAuthRefreshableService['isLoggedIn'] = () => {
    return Boolean(this._credentials);
  };

  isRedirect: Bluebeam.Services.IAuthRefreshableService['isRedirect'] = () => {
    return Promise.resolve(
      window.location.pathname.includes('auth') &&
        window.location.search.includes('code=')
    );
  };

  hasExternalCredentials: Bluebeam.Services.IAuthRefreshableService['hasExternalCredentials'] =
    () => {
      const canRouteUseExternalCredentials =
        window.location.pathname.startsWith('/labs/search/project/');

      return Promise.resolve(
        canRouteUseExternalCredentials &&
          window.location.hash.includes('accessToken=') &&
          window.location.hash.includes('idToken=')
      );
    };

  loginWithExternalCredentials: Bluebeam.Services.IAuthRefreshableService['loginWithExternalCredentials'] =
    async () => {
      const { accessToken, idToken } = qs.parse(window.location.hash.slice(1));

      if (typeof accessToken !== 'string') {
        return Promise.reject(new Error('The access token is invalid'));
      }

      if (typeof idToken !== 'string') {
        return Promise.reject(new Error('The id token is invalid'));
      }

      const credentials = convertTokensToCredentials(accessToken, idToken);

      areCredentialsValid(credentials);

      this._credentials = credentials;

      return Promise.resolve();
    };

  login: Bluebeam.Services.IAuthRefreshableService['login'] = async () => {
    const { code, state: encodedState } = qs.parse(
      window.location.search.slice(1)
    );

    if (typeof code !== 'string') {
      return Promise.reject(new Error('The auth code is invalid'));
    }

    if (typeof encodedState !== 'string') {
      return Promise.reject(new Error('The state is invalid'));
    }

    const authCode = code.trim();

    const tokens = await this._exchangeCodeForTokens(authCode);

    const credentials = convertTokensToCredentials(
      tokens.access_token,
      tokens.id_token,
      tokens.refresh_token
    );

    areCredentialsValid(credentials);

    const issuer = getClaims(credentials.accessToken).iss;
    this._deviceStorageService.setItem('bbid_issuer', issuer);

    this._credentials = credentials;

    const state = decodeState(encodedState);

    this._history.replace(`${state.pathName}${state.search ?? ''}`);

    return Promise.resolve();
  };

  logout: Bluebeam.Services.IAuthRefreshableService['logout'] = async () => {
    const logoutQuery = qs.stringify({
      client_id: environmentVars.BB_OPENID_CLIENT_ID,
      id_token_hint: this._credentials?.idToken,
      post_logout_redirect_uri: `${window.location.origin}`,
    });

    this._credentials = undefined;

    this._deviceStorageService.removeItem('bbid_issuer');

    window.location.assign(
      `${environmentVars.BB_OPENID_ENDPOINT}/connect/endsession?${logoutQuery}`
    );
  };

  redirect: Bluebeam.Services.IAuthRefreshableService['redirect'] =
    async () => {
      const state = generateState();

      let redirectUrl = `${environmentVars.BB_OPENID_ENDPOINT}/connect/authorize`;

      // Bluebeam Identity Gateway does not recognize when an Okta account has an active session.
      // WORKAROUND:
      // If the user is signed in with an Okta account, redirect to Okta instead of Bluebeam Identity Gateway
      // on subsequent visits. That way, the user doesn't have to sign in every time they refresh the page
      // if their session is still active.
      const issuer = await this._deviceStorageService.getItem<string>(
        'bbid_issuer'
      );
      if (issuer && issuer.length > 0) {
        const issuerUrl = new URL(issuer);
        if (
          issuerUrl &&
          (issuerUrl.hostname.toLowerCase().includes('okta') ||
            issuerUrl.hostname.toLowerCase().includes('signin.bluebeam'))
        ) {
          redirectUrl = `${issuerUrl}/v1/authorize`;
        }
      }

      const challengePair = await generatePkceChallenge();

      await this._deviceStorageService.setItem(
        'bbid-pkce-code-verifier',
        challengePair.codeVerifier
      );

      const redirectQueryString = qs.stringify({
        client_id: environmentVars.BB_OPENID_CLIENT_ID,
        code_challenge_method: 'S256',
        code_challenge: challengePair.codeChallenge,
        redirect_uri: `${window.location.origin}/auth`,
        response_type: 'code',
        scope: 'openid offline_access profile email',
        state: encodeState(state),
      });

      window.location.assign(`${redirectUrl}?${redirectQueryString}`);

      return Promise.resolve();
    };

  refreshTokens: Bluebeam.Services.IAuthRefreshableService['refreshTokens'] =
    async () => {
      const tokenEndpoint = `${environmentVars.BB_OPENID_ENDPOINT}/connect/token`;

      if (!this._credentials) {
        throw new Error('The user is currently logged out.');
      }

      if (!this._credentials.refreshToken) {
        throw new Error('No valid refresh token found.');
      }

      const formBody = new URLSearchParams();

      formBody.append('client_id', environmentVars.BB_OPENID_CLIENT_ID);
      formBody.append('client_secret', environmentVars.BB_OPENID_CLIENT_SECRET);
      formBody.append('refresh_token', this._credentials.refreshToken);
      formBody.append('grant_type', 'refresh_token');

      const tokensResponse: Bluebeam.Auth.API.Tokens = await fetch(
        tokenEndpoint,
        {
          body: formBody.toString(),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          method: 'POST',
        }
      ).then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }

        return response.json();
      });

      const sanitizedAccessToken =
        typeof tokensResponse.access_token === 'string'
          ? tokensResponse.access_token.trim()
          : '';
      const sanitizedIdToken =
        typeof tokensResponse.id_token === 'string'
          ? tokensResponse.id_token.trim()
          : '';
      const sanitizedRefreshToken =
        typeof tokensResponse.refresh_token === 'string'
          ? tokensResponse.refresh_token.trim()
          : '';

      if (!sanitizedAccessToken) {
        throw new Error('The new access token is invalid');
      }

      if (!sanitizedIdToken) {
        throw new Error('The new id token is invalid');
      }

      if (!sanitizedRefreshToken) {
        throw new Error('The new refresh token is invalid');
      }

      const credentials = convertTokensToCredentials(
        sanitizedAccessToken,
        sanitizedIdToken,
        sanitizedRefreshToken
      );

      areCredentialsValid(credentials);

      this._credentials = credentials;
    };

  setCredentials: Bluebeam.Services.IAuthRefreshableService['setCredentials'] =
    (tokens) => {
      const credentials = convertTokensToCredentials(
        tokens.accessToken,
        tokens.idToken,
        tokens.refreshToken
      );

      areCredentialsValid(credentials);

      this._credentials = credentials;
    };

  areCredentialsExpired: Bluebeam.Services.IAuthRefreshableService['areCredentialsExpired'] =
    () => {
      return this.getSecondsUntilAccessTokenExpiration() <= 0;
    };
}
